/**
 * apps/web :: components/whatsnext :: VehicleRegistrationSection
 *
 * Section 5 of the post-pass journey (ROADS-431).
 *
 * Per-state vehicle-registration + plates. Per-state data isn't seeded
 * yet (that lands in ROADS-438), so every state currently shows a
 * "coming soon" framing rather than guessing fees / portals.
 *
 * Why we render the section at all instead of hiding it:
 *   The journey arc is the product — skipping a step would suggest the
 *   step doesn't exist. Showing "coming soon" with a clear "what to
 *   expect" overview is honest and orients the learner. When ROADS-438
 *   lands the per-state registration block can drop in here without
 *   restructuring the page.
 */
import { useTranslation } from "react-i18next";

interface VehicleRegistrationSectionProps {
  readonly stateCode: string;
}

export default function VehicleRegistrationSection({
  stateCode,
}: VehicleRegistrationSectionProps) {
  const { t } = useTranslation();
  return (
    <section
      data-testid="whatsnext-section-registration"
      data-state={stateCode}
      data-status="coming-soon"
      aria-labelledby="whatsnext-registration-heading"
      className="rounded-lg border border-neutral-200 bg-white p-md sm:p-lg"
    >
      <h2
        id="whatsnext-registration-heading"
        className="font-display text-xl font-bold text-brand-primary"
      >
        {t("whatsnext.registration.heading")}
      </h2>
      <p className="mt-sm text-neutral-700">
        {t("whatsnext.registration.intro")}
      </p>
      <ul
        className="mt-md list-disc space-y-xs pl-md text-sm text-neutral-800"
        data-testid="whatsnext-registration-bullets"
      >
        <li>{t("whatsnext.registration.bullet.title")}</li>
        <li>{t("whatsnext.registration.bullet.proofs")}</li>
        <li>{t("whatsnext.registration.bullet.fees")}</li>
        <li>{t("whatsnext.registration.bullet.plates")}</li>
      </ul>
      <p className="mt-md rounded-md border border-amber-300 bg-amber-50 p-sm text-sm text-amber-900">
        {t("whatsnext.registration.comingSoon", { state: stateCode })}
      </p>
    </section>
  );
}
